/*
 * This file is part of the https://gitlab-intern.easd-support.de/easd/p26s26_energie_watchdog project.
 *
 * (c) 2026 EA Systems Dresden GmbH
 *
 * For the full copyright and license information, please view the LICENSE.md
 * file that was distributed with this source code.
 */

import { LineChart } from 'lucide-react';
import { useState } from 'react';
import { Navigate, useParams } from 'react-router';

/* Import i18n functions */
import { useI18n } from '@/context/I18nContext';

/* Import configuration */
import { APP_ROUTES } from '@/config/appRoutes';
import { MUENSTER_DEVICE_SENSOR_CONFIG, NEUTRAUBLING_DEVICE_SENSOR_CONFIG } from '@/config/deviceSensorConfig';
import { PLOTLY_MIN_WIDTH } from '@/config/app';
import { createBaseLoadDetailKpiConfigs } from '@/config/kpi/detailBaseLoad';

/* Import components */
import Api2StoredPlotlyPlot from '@/components/api/Api2StoredPlotlyPlot';
import Api2TimeSeriesPlot from '@/components/api/Api2TimeSeriesPlot';
import KpiCarouselApi2TimeSeries from '@/components/api/KpiCarouselApi2TimeSeries';
import KpiValueApi2TimeSeries from '@/components/api/KpiValueApi2TimeSeries';
import DashboardGrid from "@/components/layout/DashboardGrid";

/* Import libraries */
import { DeviceSensor } from '@/lib/sensors/catalog/deviceSensor';
import { DeviceSensorCatalog } from '@/lib/sensors/catalog/deviceSensorCatalog';
import { getSensorHistoryAxisRange } from '@/lib/timeSeries/sensorHistoryAxisRange';

function BaseLoadDetailTab() {
    const { t } = useI18n();
    const { baseLoadKey } = useParams<{ baseLoadKey: string }>();
    const [selectedChart, setSelectedChart] = useState<
        'sensor-history-3months' | 'sensor-history-12months' | 'prediction-14days' | 'prediction-all'
    >('sensor-history-3months');

    const baseLoadDeviceSensorCatalog = new DeviceSensorCatalog(baseLoadKey?.startsWith('Mue') ? MUENSTER_DEVICE_SENSOR_CONFIG : NEUTRAUBLING_DEVICE_SENSOR_CONFIG);
    const baseLoadSystemKeys = baseLoadDeviceSensorCatalog.getVirtualDevices('BL');
    const selectedBaseLoadKey = baseLoadKey && baseLoadSystemKeys.includes(baseLoadKey) ? baseLoadKey : null;
    const selectedBaseLoadDeviceSensor = selectedBaseLoadKey ? new DeviceSensor(selectedBaseLoadKey, baseLoadDeviceSensorCatalog) : null;

    if (!selectedBaseLoadKey || !selectedBaseLoadDeviceSensor) {
        return <Navigate to={APP_ROUTES.baseload} replace />;
    }

    const baseLoadSystemName = selectedBaseLoadDeviceSensor.getDeviceName() ?? selectedBaseLoadKey;
    const sensorHistoryMonths = selectedChart === 'sensor-history-3months' ? 3 : 12;
    const kpiConfigs = createBaseLoadDetailKpiConfigs({
        deviceSensor: selectedBaseLoadDeviceSensor,
        t: t,
    });

    if (kpiConfigs === null) {
        return <Navigate to={APP_ROUTES.baseload} replace />;
    }

    return (
        <div className="flex h-full min-h-0 flex-col gap-4">
            <div className="flex shrink-0 flex-wrap items-center justify-between gap-4 text-sm font-medium text-base-content/75">
                <span>
                    <strong>{baseLoadSystemName}:</strong> {selectedBaseLoadKey}
                </span>
                <div className="chart-selection" role="group" aria-label={t('Chart selection')}>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'sensor-history-3months' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'sensor-history-3months'}
                        title={t('Show power history for 3 months')}
                        onClick={() => setSelectedChart('sensor-history-3months')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Power history (3 months)')}
                    </button>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'sensor-history-12months' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'sensor-history-12months'}
                        title={t('Show power history for 12 months')}
                        onClick={() => setSelectedChart('sensor-history-12months')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Power history (12 months)')}
                    </button>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'prediction-14days' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'prediction-14days'}
                        title={t('Base load prediction (14d)')}
                        onClick={() => setSelectedChart('prediction-14days')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Base load prediction (14d)')}
                    </button>
                    <button
                        type="button"
                        className={`chart-selection__button ${selectedChart === 'prediction-all' ? 'chart-selection__button--active' : ''}`}
                        aria-pressed={selectedChart === 'prediction-all'}
                        title={t('Base load prediction (all)')}
                        onClick={() => setSelectedChart('prediction-all')}
                    >
                        <LineChart className="h-4 w-4" aria-hidden="true" />
                        {t('Base load prediction (all)')}
                    </button>
                </div>
            </div>
            <DashboardGrid className="min-h-0 flex-1" desktopCols={3} desktopRows={["150px", "1fr"]} breakpoint={"2xl"} aria-label={t('Base load dashboard')}>
                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries
                        {...kpiConfigs.currentCost}
                    />
                </DashboardGrid.Item>

                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiValueApi2TimeSeries
                        {...kpiConfigs.costLast14Days}
                    />
                </DashboardGrid.Item>

                <DashboardGrid.Item as="section" className="dashboard-panel">
                    <KpiCarouselApi2TimeSeries
                        {...kpiConfigs.costOverall}
                    />
                </DashboardGrid.Item>

                {/* Scrollable panels require a matching dashboard path in src/config/appPageLayout.ts. */}
                <DashboardGrid.Item as="section" className="dashboard-panel" desktopColSpan={3} mobileMinHeight={"500px"} scrollable>
                    {selectedChart === 'prediction-14days' || selectedChart === 'prediction-all' ? (
                        <Api2StoredPlotlyPlot
                            urn={selectedBaseLoadKey}
                            range={selectedChart === 'prediction-14days' ? '14d' : 'all'}
                            title={t('Base load prediction {key}', { key: selectedBaseLoadKey })}
                            minWidth={PLOTLY_MIN_WIDTH}
                        />
                    ) : (
                        <Api2TimeSeriesPlot
                            from={sensorHistoryMonths === 3 ? 'NOW-3MONTHS' : 'NOW-12MONTHS'}
                            to="NOW"
                            urn={selectedBaseLoadKey}
                            includeZeroYAxis
                            legendNames={{
                                [selectedBaseLoadKey]: t('Power history {key}', { key: selectedBaseLoadKey }),
                            }}
                            minWidth={PLOTLY_MIN_WIDTH}
                            title={t('Power history {key}', { key: selectedBaseLoadKey })}
                            xAxisTitle={t('Period')}
                            xAxisRange={getSensorHistoryAxisRange(sensorHistoryMonths)}
                            yAxisTitle={t('Power in kW')}
                        />
                    )}
                </DashboardGrid.Item>
            </DashboardGrid>
        </div>
    );
}

export default BaseLoadDetailTab;
